import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppVersionService, ApplicationInfo, AuthenticationService } from '../../domain';
import { TestClockModalComponent } from '../test-clock-modal/test-clock-modal.component';

/**
 * Thin full-width status strip rendered above the app header in NON-production
 * environments only. Surfaces environment, notification delivery mode, build
 * version/timestamp, and the active date. The date item shows the real wall-clock
 * date when no simulation is set and the simulated date when it is; admins can
 * click it to open the Test Clock modal.
 */
@Component({
    selector: 'status-banner',
    templateUrl: './status-banner.component.html',
    styleUrls: ['./status-banner.component.scss']
})
export class StatusBannerComponent implements OnDestroy {
    public canEditClock = false;
    private sub: Subscription;

    constructor(
        private appVersion: AppVersionService,
        private dialog: MatDialog,
        auth: AuthenticationService
    ) {
        // Clickable for AHR admins, and for admins who are currently proxying
        // (isProxied ⇒ a PPE admin is behind the session). The backend performs
        // the authoritative check; this gate only decides whether to show the modal.
        this.sub = auth.currentIdentity.subscribe(identity =>
            this.canEditClock = identity.match(
                () => false,
                i => i.user.roles.academicHumanResources || i.user.isProxied));
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    private get info(): ApplicationInfo {
        return this.appVersion.serverInfo || {};
    }

    public get isProduction(): boolean {
        // Fail closed: hide the banner unless we POSITIVELY know the environment
        // is non-production. A failed /version fetch leaves environment empty
        // (serverInfo falls back to {}); treating unknown as prod prevents the
        // banner from leaking into production on that error path.
        const env = (this.info.environment || '').toLowerCase();
        return env === '' || env === 'production';
    }

    public get environment(): string {
        return this.info.environment || '—';
    }

    public get deliveryMode(): string {
        return this.info.notificationDeliveryMode || '—';
    }

    public get version(): string {
        return this.info.version || '—';
    }

    public get buildTimestamp(): string {
        return this.info.buildTimestamp || '—';
    }

    public get simulatedDate(): string | null {
        return this.info.simulatedDate || null;
    }

    public get dateLabel(): string {
        return this.simulatedDate ? 'Simulated date:' : 'Date:';
    }

    public get dateValue(): string {
        // Simulated value comes from the server (YYYY-MM-DD); when unset, show the
        // client's wall-clock date in the same format.
        if (this.simulatedDate) {
            return this.simulatedDate;
        }
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        return `${now.getFullYear()}-${mm}-${dd}`;
    }

    public openClockDialog(): void {
        if (!this.canEditClock) {
            return;
        }
        this.dialog.open(TestClockModalComponent, { autoFocus: false });
    }
}
